"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, Route, Zap, TrendingUp, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  phase: "01",
  title: "Upload Property Photos",
  desc: "Simply upload your existing property photos to our secure platform. Our AI immediately begins analyzing lighting, composition, and spatial depth.",
  icon: Search
}, {
  phase: "02",
  title: "AI Enhancement",
  desc: "Our proprietary algorithms apply luxury-grade retouching, virtual staging, and lighting corrections to transform standard rooms into premium suites.",
  icon: Route
}, {
  phase: "03",
  title: "Review & Refine",
  desc: "Browse your enhanced gallery. Our system ensures every detail aligns with high-end hospitality standards before you finalize the selection.",
  icon: Zap
}, {
  phase: "04",
  title: "Convert & Book",
  desc: "Deploy your new visuals across Booking.com, Airbnb, and your website. Watch as your conversion rates and average daily rates begin to climb.",
  icon: TrendingUp
}];
function TimelineStep({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const isEven = index % 2 === 0;
  return <div data-index={index} ref={ref} className="relative flex flex-col md:flex-row items-center w-full mb-32 last:mb-0 group">
      {}
      {!isLast && <div className={cn("absolute left-6 md:left-1/2 top-8 w-0.5 bg-secondary -translate-x-1/2 transition-all duration-1000 ease-out origin-top z-0", isVisible ? "h-[calc(100%+8rem)] shadow-md opacity-100" : "h-0 opacity-0")} />}

      {}
      <div className={cn("absolute left-6 md:left-1/2 top-8 -translate-x-1/2 w-5 h-5 rotate-45 rounded-md z-10 transition-all duration-700 flex items-center justify-center", isVisible ? "bg-secondary shadow-lg scale-110" : "bg-background border-2 border-border scale-100")}>
        <div className={cn("w-1.5 h-1.5 rounded-md transition-colors duration-700", isVisible ? "bg-background" : "bg-transparent")} />
      </div>

      {}
      <div className={cn("w-full md:w-1/2 pl-16 md:pl-0", isEven ? "md:pr-16" : "md:pl-16 md:ml-auto")}>
        <Card className={cn("transition-all duration-700 transform relative overflow-hidden border-border/60 bg-card", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", isVisible && "border-secondary/40 shadow-md")}>
          {}
          <div className={cn("absolute inset-0 bg-secondary/5 transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")} />
          <CardHeader className="relative z-10 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <Badge variant="outline" className="border-secondary/50 text-secondary bg-secondary/5 font-mono tracking-widest text-xs">
                PHASE {step.phase}
              </Badge>
              <step.icon className="w-5 h-5 text-secondary" />
            </div>
            <CardTitle className="text-2xl font-default font-semibold tracking-tight text-foreground">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <p className="text-muted-foreground leading-relaxed font-default">
              {step.desc}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheAiAlchemy() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="261281" id="the-ai-alchemy" className="dark bg-background text-foreground py-24 md:py-32 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-lg blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-24 transition-all duration-700 transform", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tight">
            The AI <span className="text-secondary">Alchemy</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            A sophisticated process designed for the modern hotelier. We transform 
            standard property photography into high-converting visual assets.
          </p>
        </div>

        {}
        <div className="relative max-w-4xl mx-auto">
          {}
          <div className="absolute left-6 md:left-1/2 top-8 bottom-8 w-0.5 bg-border/20 -translate-x-1/2 z-0" />

          {}
          <div className="relative z-10">
            {steps.map((step, index) => <TimelineStep key={index} step={step} index={index} isLast={index === steps.length - 1} />)}
          </div>
        </div>

        {}
        <div className="mt-24 text-center">
          <Link to="/HomePage">
            <Button size="lg" className="group rounded-md px-8 h-12 text-base bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all shadow-md">
              Elevate Your Property
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}